import React, { useState } from 'react';
import styled from 'styled-components';
import { useAuth } from './AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { LogIn, ExternalLink } from 'lucide-react';
import { StyledButton as CommonStyledButton, AlertBox } from '../main/CommonStyles';
import { AppConfig } from '../config';
import authService from '../services/authService';

const StyledForm = styled.form`
    display: flex;
    flex-direction: column;
    gap: 1rem;
    width: 100%;
`;

const StyledInput = styled.input`
    padding: 0.75rem;
    border: 1px solid ${({ theme }) => theme.colors.borderColor};
    border-radius: ${({ theme }) => theme.borderRadius};
    font-size: 1rem;
`;

const OAuthSeparator = styled.div`
    display: flex;
    align-items: center;
    text-align: center;
    color: ${({ theme }) => theme.colors.textMedium};
    font-size: 0.85rem;
    margin: 1rem 0;

    &::before, &::after {
        content: '';
        flex: 1;
        border-bottom: 1px solid ${({ theme }) => theme.colors.borderColor};
    }
    &:not(:empty)::before { margin-right: .5em; }
    &:not(:empty)::after { margin-left: .5em; }
`;

const OAuthButton = styled(CommonStyledButton)`
    background-color: ${({ theme, provider }) => provider === 'google' ? '#DB4437' : '#4267B2'};
    color: white;
    width: 100%;
    justify-content: center;

    &:hover {
        background-color: ${({ theme, provider }) => provider === 'google' ? '#C33D2E' : '#365899'};
    }
`;

const StandardLoginForm = ({ initialError }) => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState(initialError || '');
    const [loading, setLoading] = useState(false);
    const { login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            await login(username, password);
            const from = location.state?.from?.pathname || '/app';
            navigate(from, { replace: true });
        } catch (err) {
            setError(err.response?.data?.message || 'Logowanie nie powiodło się.');
        }
        setLoading(false);
    };

    return (
        <>
            {error && <AlertBox type="error" style={{ marginBottom: '1rem' }}>{error}</AlertBox>}
            <StyledForm onSubmit={handleSubmit}>
                <StyledInput
                    type="text"
                    placeholder="Nazwa użytkownika lub e-mail"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                    disabled={loading}
                />
                <StyledInput
                    type="password"
                    placeholder="Hasło"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={loading}
                />
                <CommonStyledButton type="submit" disabled={loading} style={{ width: '100%' }}>
                    <LogIn size={18} />
                    {loading ? 'Logowanie...' : 'Zaloguj się'}
                </CommonStyledButton>
            </StyledForm>

            {(AppConfig.auth.ENABLE_OAUTH_GOOGLE || AppConfig.auth.ENABLE_OAUTH_FACEBOOK) && (
                <>
                    <OAuthSeparator>lub</OAuthSeparator>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', width: '100%' }}>
                        {AppConfig.auth.ENABLE_OAUTH_GOOGLE && (
                            <OAuthButton as="a" href={authService.OAUTH2_LOGIN_URL_GOOGLE} provider="google">
                                <ExternalLink size={18} /> Kontynuuj z Google
                            </OAuthButton>
                        )}
                        {AppConfig.auth.ENABLE_OAUTH_FACEBOOK && (
                            <OAuthButton as="a" href={authService.OAUTH2_LOGIN_URL_FACEBOOK} provider="facebook">
                                <ExternalLink size={18} /> Kontynuuj z Facebook
                            </OAuthButton>
                        )}
                    </div>
                </>
            )}
        </>
    );
};

export default StandardLoginForm;